import React, { createContext, useContext, useState, useEffect } from 'react';
import { format, parse, isBefore, isValid, setYear, getYear } from 'date-fns';
import { publicDataFetch } from '../api';

const AppContext = createContext();

// Constant owner_id for full CRUD without login, using public_scoped_readwrite policies.
const ADMIN_OWNER_ID = '9jRopCmAnptBRhLjWKi1QqcRxCjnSShI';

// Helper to safely extract IDs from NoCodeBackend responses, which might return
// populated relational objects { id: '...', name: '...' } instead of just the ID string.
const extractId = (val) => {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'object') return (val.id || val._id || '').toString();
  return val.toString();
};

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [plans, setPlans] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [tags, setTags] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncError, setSyncError] = useState(null);

  const loadData = async () => {
    setIsLoading(true);
    setSyncError(null);
    try {
      // Use pagination to fetch all records safely without hitting max limit errors.
      const fetchAll = async (path) => {
        let allData = [];
        let page = 1;
        let hasMore = true;
        
        while (hasMore) {
          const res = await publicDataFetch(`${path}&page=${page}&limit=100`);
          if (res?.status === 'success') {
            allData = [...allData, ...(res.data || [])];
            hasMore = res.metadata?.hasMore ?? false;
            page++;
          } else {
            if (page === 1) return res; 
            hasMore = false;
          }
        }
        return { status: 'success', data: allData };
      };

      const [pRes, agRes, tRes, cRes, tskRes] = await Promise.all([
        fetchAll(`read/plans?owner_id=${ADMIN_OWNER_ID}`),
        fetchAll(`read/agencies?owner_id=${ADMIN_OWNER_ID}`),
        fetchAll(`read/tags?owner_id=${ADMIN_OWNER_ID}`),
        fetchAll(`read/clients?owner_id=${ADMIN_OWNER_ID}`),
        fetchAll(`read/tasks?owner_id=${ADMIN_OWNER_ID}`),
      ]);

      if (pRes?.status === 'success') {
        const plansData = (pRes.data ?? []).map(p => ({
          ...p,
          id: extractId(p.id),
          name: p.name || 'Unnamed Plan',
          color: p.color || '#14b8a6',
          monthlyHours: parseFloat(p.monthlyhours ?? p.monthlyHours ?? p.monthly_hours ?? 0) || 0,
        })).sort((a, b) => (a.order_value ?? 0) - (b.order_value ?? 0));
        setPlans(plansData);
      }

      if (agRes?.status === 'success') {
        const agenciesData = (agRes.data ?? []).map(a => ({
          ...a,
          id: extractId(a.id),
          name: a.name || 'Unnamed Agency',
          color: a.color || '#14b8a6',
          password: a.password || ''
        })).sort((a, b) => (a.order_value ?? 0) - (b.order_value ?? 0));
        setAgencies(agenciesData);
      }

      if (tRes?.status === 'success') {
        const tagsData = (tRes.data ?? []).map(t => ({
          ...t,
          id: extractId(t.id),
          name: t.name || 'Unnamed Tag',
          color: t.color || '#14b8a6'
        })).sort((a, b) => (a.order_value ?? 0) - (b.order_value ?? 0));
        setTags(tagsData);
      }

      const resetEvents = [];
      
      if (cRes?.status === 'success') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const clientsData = (cRes.data ?? []).map(c => {
          let clientTags = [];
          const rawTags = c.tags ?? c.Tags;
          
          if (Array.isArray(rawTags)) {
            clientTags = rawTags.map(t => extractId(t)).filter(Boolean);
          } else if (typeof rawTags === 'string' && rawTags.trim()) {
            if (rawTags.startsWith('[') && rawTags.endsWith(']')) {
              try { 
                const parsed = JSON.parse(rawTags);
                clientTags = Array.isArray(parsed) ? parsed.map(t => extractId(t)).filter(Boolean) : [];
              } catch (e) { clientTags = []; }
            } else {
              clientTags = rawTags.split(',').map(t => extractId(t.trim())).filter(Boolean);
            }
          }
          
          let currentResetDate = c.resetdate ?? c.resetDate ?? c.reset_date ?? '';
          let currentUsedHours = parseFloat(c.usedhours ?? c.usedHours ?? c.used_hours ?? 0) || 0;
          let needsUpdate = false;

          // Parse DD/MM/YYYY
          let parsedDate = parse(currentResetDate, 'dd/MM/yyyy', new Date());
          
          // Fallback for older formats (e.g. MM-dd or MM/dd/yyyy)
          if (!isValid(parsedDate)) {
             let oldParsed = parse(currentResetDate, 'MM-dd', new Date());
             if (isValid(oldParsed)) {
                 parsedDate = setYear(oldParsed, getYear(today));
             } else {
                 oldParsed = parse(currentResetDate, 'MM/dd/yyyy', new Date());
                 if (isValid(oldParsed)) parsedDate = oldParsed;
             }
          }

          // Check if date has passed
          if (isValid(parsedDate)) {
             parsedDate.setHours(0, 0, 0, 0);

             // FIX: Revert dates that were accidentally bumped to 2027 if they wouldn't have passed yet in 2026
             if (getYear(parsedDate) === 2027) {
                 let possible2026 = setYear(parsedDate, 2026);
                 if (!isBefore(possible2026, today)) {
                     parsedDate = possible2026;
                     needsUpdate = true;
                 }
             }

             let hasPassed = !isBefore(today, parsedDate);

             if (hasPassed) {
                 resetEvents.push({
                   clientId: extractId(c.id),
                   resetDate: format(parsedDate, 'dd/MM/yyyy'),
                   resetHours: currentUsedHours
                 });

                 // Reset hours because the cycle passed
                 currentUsedHours = 0;
                 
                 // Bump to 2027 as requested (or next year if we are past 2027)
                 let nextYear = getYear(today) + 1;
                 if (nextYear < 2027) nextYear = 2027;
                 
                 parsedDate = setYear(parsedDate, nextYear);
                 needsUpdate = true;
             }
             
             // Normalize formatting
             const formattedDate = format(parsedDate, 'dd/MM/yyyy');
             if (formattedDate !== currentResetDate) {
                 currentResetDate = formattedDate;
                 needsUpdate = true;
             }
          } else {
             // If completely invalid, default to today in 2027
             currentResetDate = format(setYear(new Date(), 2027), 'dd/MM/yyyy');
             needsUpdate = true;
          }

          const clientObj = { 
            ...c, 
            id: extractId(c.id),
            name: c.name || 'Unnamed Client',
            website: c.website || '',
            notes: c.notes || '',
            domain: c.domain || '',
            tags: clientTags,
            planId: extractId(c.planid ?? c.planId ?? c.plan_id),
            agencyId: extractId(c.agencyid ?? c.agencyId ?? c.agency_id),
            usedHours: currentUsedHours,
            resetDate: currentResetDate
          };

          // Auto-sync back to database if we changed it automatically
          if (needsUpdate) {
             publicDataFetch(`update/clients/${clientObj.id}?owner_id=${ADMIN_OWNER_ID}`, {
                method: 'PUT',
                body: JSON.stringify({ 
                   usedhours: currentUsedHours, 
                   resetdate: currentResetDate,
                   user_id: ADMIN_OWNER_ID 
                })
             }).catch(e => console.error("Auto-sync for client reset date failed", e));
          }

          return clientObj;
        });
        setClients(clientsData);
      }

      if (tskRes?.status === 'success') {
        const tasksData = (tskRes.data ?? []).map(t => {
          let taskMessages = [];
          if (typeof t.messages === 'string' && t.messages.trim()) {
            try { taskMessages = JSON.parse(t.messages); } catch (e) { taskMessages = []; }
          } else {
            taskMessages = Array.isArray(t.messages) ? t.messages : [];
          }
          return { 
            ...t, 
            id: extractId(t.id),
            title: t.title || 'Untitled Task',
            status: t.status || 'Open',
            priority: t.priority || 'Medium',
            date: t.date || new Date().toISOString(),
            last_updated: t.last_updated || t.date || new Date().toISOString(),
            clientId: extractId(t.clientid ?? t.clientId ?? t.client_id), 
            messages: taskMessages, 
            loggedHours: parseFloat(t.loggedhours ?? t.loggedHours ?? t.logged_hours ?? 0) || 0
          };
        });
        setTasks(tasksData);

        const resetTasks = [];
        for (const resetEvent of resetEvents) {
          const title = `Support hours reset: ${resetEvent.resetHours.toFixed(1)}h on ${resetEvent.resetDate}`;
          const alreadyLogged = tasksData.some(task => (
            task.clientId === resetEvent.clientId && task.title === title
          ));

          if (alreadyLogged) continue;

          const [day, month, year] = resetEvent.resetDate.split('/');
          const resetTimestamp = new Date(Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            12
          )).toISOString();

          const payload = {
            title,
            date: resetTimestamp,
            status: 'Closed',
            priority: 'Low',
            loggedhours: 0,
            messages: JSON.stringify([]),
            user_id: ADMIN_OWNER_ID,
            clientid: resetEvent.clientId,
            last_updated: resetTimestamp
          };

          const res = await publicDataFetch(`create/tasks?owner_id=${ADMIN_OWNER_ID}`, {
            method: 'POST',
            body: JSON.stringify(payload)
          });

          if (res?.status === 'success') {
            resetTasks.push({
              ...payload,
              id: extractId(res?.data?.id),
              messages: [],
              clientId: extractId(payload.clientid),
              loggedHours: payload.loggedhours
            });
          }
        }

        if (resetTasks.length > 0) {
          setTasks(prev => [...prev, ...resetTasks]);
        }
      }
      
    } catch (error) {
      console.error("Sync Error:", error);
      setSyncError("Network error while syncing with database.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getClientUsedHours = (clientId) => {
    const client = clients.find(c => c.id === extractId(clientId));
    return client ? parseFloat(client.usedHours || 0) : 0;
  };

  const addClient = async (clientData) => {
    const payload = {
      name: clientData.name,
      website: clientData.website || '',
      notes: clientData.notes || '',
      domain: clientData.domain || '',
      tags: JSON.stringify(clientData.tags || []),
      resetdate: clientData.resetDate || format(new Date(), 'dd/MM/yyyy'),
      usedhours: parseFloat(clientData.usedHours || 0) || 0,
      planid: clientData.planId || null,
      agencyid: clientData.agencyId || null,
      user_id: ADMIN_OWNER_ID
    };
    const res = await publicDataFetch(`create/clients?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      const newClient = { 
        ...payload, 
        id: extractId(res.data.id), 
        tags: clientData.tags || [],
        planId: extractId(payload.planid),
        agencyId: extractId(payload.agencyid),
        usedHours: payload.usedhours,
        resetDate: payload.resetdate
      };
      setClients(prev => [...prev, newClient]);
    }
  };

  const bulkAddClients = async (clientsArray) => {
    for (const client of clientsArray) {
      await addClient(client);
    }
  };

  const updateClient = async (id, updates) => {
    const payload = { user_id: ADMIN_OWNER_ID };
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.website !== undefined) payload.website = updates.website;
    if (updates.notes !== undefined) payload.notes = updates.notes;
    if (updates.domain !== undefined) payload.domain = updates.domain;
    if (updates.tags !== undefined) payload.tags = JSON.stringify(updates.tags);
    if (updates.resetDate !== undefined) payload.resetdate = updates.resetDate;
    if (updates.usedHours !== undefined) payload.usedhours = parseFloat(updates.usedHours) || 0;
    if (updates.planId !== undefined) payload.planid = updates.planId || null;
    if (updates.agencyId !== undefined) payload.agencyid = updates.agencyId || null;

    const res = await publicDataFetch(`update/clients/${id}?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      setClients(prev => prev.map(c => c.id === extractId(id) ? { ...c, ...updates } : c));
    }
  };

  const removeClient = async (id) => {
    const res = await publicDataFetch(`delete/clients/${id}?owner_id=${ADMIN_OWNER_ID}`, { method: 'DELETE' });
    if (res?.status === 'success') {
      setClients(prev => prev.filter(c => c.id !== extractId(id)));
    }
  };

  const addTask = async (taskData) => {
    const now = new Date().toISOString();
    const payload = {
      title: taskData.title,
      clientid: taskData.clientId || null,
      status: taskData.status || 'Open',
      priority: taskData.priority || 'Medium',
      loggedhours: parseFloat(taskData.loggedHours) || 0,
      messages: JSON.stringify([]),
      date: now,
      last_updated: now,
      user_id: ADMIN_OWNER_ID
    };
    const res = await publicDataFetch(`create/tasks?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      const newTask = { 
        ...payload, 
        id: extractId(res.data.id), 
        messages: [],
        clientId: extractId(payload.clientid),
        loggedHours: payload.loggedhours
      };
      setTasks(prev => [...prev, newTask]);

      // Sync initial logged hours to client
      if (payload.loggedhours > 0 && payload.clientid) {
        const client = clients.find(c => c.id === extractId(payload.clientid));
        if (client) {
          updateClient(client.id, { usedHours: (parseFloat(client.usedHours) || 0) + payload.loggedhours });
        }
      }
    }
  };

  const updateTask = async (id, updates) => {
    const oldTask = tasks.find(t => t.id === extractId(id));
    const payload = { user_id: ADMIN_OWNER_ID };
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.priority !== undefined) payload.priority = updates.priority;
    if (updates.date !== undefined) payload.date = updates.date;
    if (updates.messages !== undefined) payload.messages = JSON.stringify(updates.messages);
    if (updates.loggedHours !== undefined) payload.loggedhours = parseFloat(updates.loggedHours) || 0;
    if (updates.clientId !== undefined) payload.clientid = updates.clientId || null;
    
    // Automatically manage last_updated if not explicitly provided
    payload.last_updated = updates.last_updated !== undefined ? updates.last_updated : new Date().toISOString();

    const res = await publicDataFetch(`update/tasks/${id}?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      setTasks(prev => prev.map(t => t.id === extractId(id) ? { ...t, ...updates, last_updated: payload.last_updated } : t));

      // Sync delta of hours to client if changed
      if (updates.loggedHours !== undefined && oldTask) {
        const oldHours = parseFloat(oldTask.loggedHours) || 0;
        const newHours = parseFloat(updates.loggedHours) || 0;
        const delta = newHours - oldHours;
        
        if (delta !== 0) {
          const clientId = updates.clientId !== undefined ? extractId(updates.clientId) : oldTask.clientId;
          const client = clients.find(c => c.id === clientId);
          if (client) {
            updateClient(client.id, { usedHours: (parseFloat(client.usedHours) || 0) + delta });
          }
        }
      }
    }
  };

  const archiveTask = async (id) => {
    await updateTask(id, { status: 'Archived' });
  };

  const removeTask = async (id) => {
    const oldTask = tasks.find(t => t.id === extractId(id));
    const res = await publicDataFetch(`delete/tasks/${id}?owner_id=${ADMIN_OWNER_ID}`, { method: 'DELETE' });
    if (res?.status === 'success') {
      setTasks(prev => prev.filter(t => t.id !== extractId(id)));

      // Subtract hours from client if task is deleted
      if (oldTask && oldTask.loggedHours > 0 && oldTask.clientId) {
        const client = clients.find(c => c.id === oldTask.clientId);
        if (client) {
          updateClient(client.id, { usedHours: Math.max(0, (parseFloat(client.usedHours) || 0) - parseFloat(oldTask.loggedHours)) });
        }
      }
    }
  };

  const addTaskMessage = async (taskId, message, hours = 0) => {
    const task = tasks.find(t => t.id === extractId(taskId));
    if (!task) return;
    const currentMessages = Array.isArray(task.messages) ? task.messages : [];
    const now = new Date().toISOString();
    const newMessages = [
      ...currentMessages,
      { ...message, id: `m${Date.now()}`, date: now, hours: parseFloat(hours) || 0 }
    ];
    await updateTask(taskId, {
      loggedHours: (parseFloat(task.loggedHours) || 0) + (parseFloat(hours) || 0),
      messages: newMessages,
      last_updated: now
    });
  };

  const addPlan = async (p) => {
    const payload = { 
      name: p.name, 
      monthlyhours: parseFloat(p.monthlyHours || 0), 
      color: p.color, 
      order_value: plans.length, 
      user_id: ADMIN_OWNER_ID
    };
    const res = await publicDataFetch(`create/plans?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      setPlans(prev => [...prev, { 
        ...payload, 
        id: extractId(res.data.id),
        monthlyHours: payload.monthlyhours
      }]);
    }
  };

  const removePlan = async (id) => {
    const res = await publicDataFetch(`delete/plans/${id}?owner_id=${ADMIN_OWNER_ID}`, { method: 'DELETE' });
    if (res?.status === 'success') {
      setPlans(prev => prev.filter(p => p.id !== extractId(id)));
    }
  };

  const addTag = async (t) => {
    const payload = { 
      name: t.name, 
      color: t.color, 
      order_value: tags.length, 
      user_id: ADMIN_OWNER_ID
    };
    const res = await publicDataFetch(`create/tags?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      setTags(prev => [...prev, { ...payload, id: extractId(res.data.id) }]);
    }
  };

  const removeTag = async (id) => {
    const res = await publicDataFetch(`delete/tags/${id}?owner_id=${ADMIN_OWNER_ID}`, { method: 'DELETE' });
    if (res?.status === 'success') {
      setTags(prev => prev.filter(t => t.id !== extractId(id)));
    }
  };

  const addAgency = async (agencyData) => {
    const payload = { 
      name: agencyData.name, 
      color: agencyData.color, 
      password: agencyData.password, 
      order_value: agencies.length, 
      user_id: ADMIN_OWNER_ID
    };
    const res = await publicDataFetch(`create/agencies?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      setAgencies(prev => [...prev, { ...payload, id: extractId(res.data.id) }]);
    }
  };

  const updateAgency = async (id, updates) => {
    const payload = { ...updates, user_id: ADMIN_OWNER_ID };
    const res = await publicDataFetch(`update/agencies/${id}?owner_id=${ADMIN_OWNER_ID}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (res?.status === 'success') {
      setAgencies(prev => prev.map(a => a.id === extractId(id) ? { ...a, ...updates } : a));
    }
  };

  const removeAgency = async (id) => {
    const res = await publicDataFetch(`delete/agencies/${id}?owner_id=${ADMIN_OWNER_ID}`, { method: 'DELETE' });
    if (res?.status === 'success') {
      setAgencies(prev => prev.filter(a => a.id !== extractId(id)));
    }
  };

  return (
    <AppContext.Provider value={{
      plans, setPlans, addPlan, removePlan,
      agencies, setAgencies, addAgency, updateAgency, removeAgency,
      tags, setTags, addTag, removeTag,
      clients, setClients, addClient, bulkAddClients, updateClient, removeClient, getClientUsedHours,
      tasks, addTask, updateTask, removeTask, addTaskMessage, archiveTask,
      isLoading, syncError, refreshData: loadData,
      adminOwnerId: ADMIN_OWNER_ID
    }}>
      {children}
    </AppContext.Provider>
  );
};